// src/components/Products/FeedbackForm/pages/CustomerForm.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { FaStar, FaArrowLeft } from 'react-icons/fa';
import {
  publicListQuestions as listQuestions,
  publicStartSession as startSession,
  publicSubmitAnswers as submitAnswers,
  publicFinishSession as finishSession,
  publicSaveFeedback as saveFeedback,
} from '../services/api';
import { getPublicRestaurante } from '../services/publicApi';
import { getApiErrorMessage } from '../../../../utils/apiError';

// ===== Config =====
const PAGE_SIZE = 3; // quantas perguntas por página

// --- Validação de telefone ---
function telefoneValido(t) {
  const digits = String(t || '').replace(/\D/g, '');
  if (digits.length < 10 || digits.length > 11) return false;
  const ddd = digits.substring(0, 2);
  if (Number(ddd) < 11 || Number(ddd) > 99) return false;
  return true;
}

// --- Hook slug ---
function useRestauranteSlugFromQuery() {
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const params = useMemo(() => new URLSearchParams(search), [search]);
  return params.get('restaurante') || '';
}

// --- Helper id ---
function getPerguntaId(p) {
  return p?.id ?? p?.pergunta_id ?? p?.perguntaId;
}

export default function CustomerForm() {
  const restauranteSlug = useRestauranteSlugFromQuery();

  // base
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [restaurante, setRestaurante] = useState(null);
  const [restauranteId, setRestauranteId] = useState(null);

  // steps: 0 confirmar, 1 identificar, >=2 formulário
  const [step, setStep] = useState(0);

  // cliente
  const [clienteNome, setClienteNome] = useState('');
  const [clienteTelefone, setClienteTelefone] = useState('');

  // sessão + perguntas
  const [clienteId, setClienteId] = useState(null);
  const [perguntas, setPerguntas] = useState([]);
  const [page, setPage] = useState(0);
  const [respostas, setRespostas] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [salvandoParcial, setSalvandoParcial] = useState(false);
  const [formError, setFormError] = useState('');
  const [finalizado, setFinalizado] = useState(false);

  // feedback final
  const [notaFinal, setNotaFinal] = useState(0);
  const [comentarioFinal, setComentarioFinal] = useState('');

  // link do Google (prioridade: ?gl > DB > localStorage)
  const [googleLink, setGoogleLink] = useState('');

  // carregar restaurante
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        if (!restauranteSlug) {
          setErro('Restaurante não informado. Verifique o link.');
          return;
        }
        // O backend deve retornar: { id, nome, google_review_url }
        const info = await getPublicRestaurante(restauranteSlug);
        if (!alive) return;
        setRestaurante(info || null);
        setRestauranteId(info?.id ?? null);
      } catch (e) {
        console.error('Falha ao carregar restaurante:', e);
        if (alive) {
          setErro(getApiErrorMessage(e, 'Não foi possível carregar o estabelecimento.'));
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [restauranteSlug]);

  // resolve googleLink: ?gl > restaurante.google_review_url > localStorage
  useEffect(() => {
    try {
      const search = typeof window !== 'undefined' ? window.location.search : '';
      const params = new URLSearchParams(search);
      const urlGl = (params.get('gl') || '').trim();
      const dbGl = (restaurante?.google_review_url || '').trim();

      let savedGl = '';
      try {
        savedGl = localStorage.getItem('googleLink') || '';
      } catch {
        // localStorage pode estar indisponível (modo privado / quota)
      }

      const chosen = urlGl || dbGl || savedGl || '';
      setGoogleLink(chosen);

      if (chosen) {
        try {
          localStorage.setItem('googleLink', chosen);
        } catch {
          // localStorage pode estar indisponível (modo privado / quota)
        }
      }
    } catch (error) {
      console.error('Erro ao resolver link do Google:', error);
      try {
        const saved = localStorage.getItem('googleLink') || '';
        setGoogleLink(saved);
      } catch {
        // localStorage pode estar indisponível (modo privado / quota)
      }
    }
  }, [restaurante]);

  async function handleConfirmarEstabelecimento() {
    setStep(1);
  }

  async function handleIdentificarCliente(e) {
    e?.preventDefault?.();
    if (!clienteNome.trim()) {
      setErro('Informe seu nome.');
      return;
    }
    if (!telefoneValido(clienteTelefone)) {
      setErro('Informe um telefone válido (com DDD).');
      return;
    }
    if (!restauranteId) {
      setErro('Restaurante inválido. Recarregue o link.');
      return;
    }

    setErro('');
    setFormError('');
    setLoading(true);
    try {
      const qs = await listQuestions(restauranteId, true);
      setPerguntas(qs || []);

      const sessao = await startSession(
        restauranteId,
        clienteTelefone.trim(),
        clienteNome.trim()
      );
      setClienteId(sessao?.id ?? sessao?.clienteId ?? null);

      setPage(0);
      setShowErrors(false);
      setStep(2);
    } catch (e) {
      console.error(e);
      setErro(getApiErrorMessage(e, 'Não foi possível iniciar o formulário.'));
    } finally {
      setLoading(false);
    }
  }

  // paginação
  const totalPaginasDePerguntas = Math.max(
    1,
    Math.ceil((perguntas?.length || 0) / PAGE_SIZE)
  );
  const showFeedback = page >= totalPaginasDePerguntas;

  const start = page * PAGE_SIZE;
  const end = start + PAGE_SIZE;
  const grupoAtual = !showFeedback ? (perguntas || []).slice(start, end) : [];

  // progresso: 2 (intro+id) + páginas de perguntas + 1 (feedback)
  const totalPassos = 2 + totalPaginasDePerguntas + 1;
  const passoAtual = step < 2 ? step : 2 + Math.min(page, totalPaginasDePerguntas);
  const progresso = (passoAtual / totalPassos) * 100;

  // respostas
  function setRespostaAtual(pergunta, valor) {
    const pid = getPerguntaId(pergunta);
    if (pid == null) return;
    const isNumber = typeof valor === 'number';
    setFormError('');
    setRespostas((prev) => ({
      ...prev,
      [pid]: isNumber
        ? { respostaNumero: valor }
        : { respostaTexto: String(valor || '') },
    }));
  }

  function toggleMultiOption(pergunta, opt) {
    const pid = getPerguntaId(pergunta);
    if (pid == null) return;

    setFormError('');
    setRespostas((prev) => {
      const cur = prev[pid]?.respostaLista || [];
      const exists = cur.includes(opt);
      const next = exists ? cur.filter((v) => v !== opt) : [...cur, opt];
      return { ...prev, [pid]: { respostaLista: next } };
    });
  }

  function isAnswered(pergunta) {
    const pid = getPerguntaId(pergunta);
    const r = respostas[pid] || {};
    switch (pergunta.tipo) {
      case 'nps':
      case 'escala':
        return typeof r.respostaNumero === 'number';
      case 'multipla_escolha_multi':
        return Array.isArray(r.respostaLista) && r.respostaLista.length > 0;
      case 'multipla_escolha':
      case 'sim_nao':
      case 'texto':
      default:
        return (r.respostaTexto ?? '').trim() !== '';
    }
  }

  function grupoValido() {
    return grupoAtual.every((p) => !p?.obrigatoria || isAnswered(p));
  }

  function buildAnswerItems(questions = perguntas) {
    const allowedIds = new Set(
      (questions || [])
        .map((pergunta) => Number(getPerguntaId(pergunta)))
        .filter((id) => Number.isFinite(id))
    );

    return Object.entries(respostas)
      .filter(([perguntaId]) => allowedIds.size === 0 || allowedIds.has(Number(perguntaId)))
      .map(([perguntaId, value]) => {
        if (Array.isArray(value.respostaLista)) {
          return { pergunta_id: Number(perguntaId), respostaLista: value.respostaLista };
        }
        if (value.respostaNumero != null) {
          return { pergunta_id: Number(perguntaId), respostaNumero: value.respostaNumero };
        }
        return { pergunta_id: Number(perguntaId), respostaTexto: value.respostaTexto || '' };
      })
      .filter((item) => (
        item.respostaNumero != null
        || String(item.respostaTexto || '').trim() !== ''
        || (Array.isArray(item.respostaLista) && item.respostaLista.length > 0)
      ));
  }

  async function salvarRespostasParciais(questions = grupoAtual) {
    if (!clienteId) throw new Error('ID da sessão do cliente é nulo.');
    const itens = buildAnswerItems(questions);
    if (!itens.length) return;
    await submitAnswers({ restauranteId, clienteId, itens });
  }

  // navegação
  function voltar() {
    setShowErrors(false);
    setFormError('');
    if (showFeedback) {
      setPage(totalPaginasDePerguntas - 1);
      return;
    }
    if (page > 0) {
      setPage((p) => p - 1);
      return;
    }
    setStep(1);
  }

  async function avancar() {
    if (!showFeedback) {
      if (!grupoValido()) {
        setShowErrors(true);
        setFormError('Responda todas as obrigatórias para continuar.');
        return;
      }
      setShowErrors(false);
      setFormError('');

      try {
        setSalvandoParcial(true);
        await salvarRespostasParciais(grupoAtual);
        setPage((p) => p + 1);
      } catch (error) {
        console.error('Erro ao salvar respostas parciais:', error);
        setFormError(getApiErrorMessage(error, 'Não foi possível salvar esta etapa. Tente novamente.'));
      } finally {
        setSalvandoParcial(false);
      }
      return;
    }

    // enviar (feedback final)
    try {
      setEnviando(true);
      if (!clienteId) throw new Error('ID da sessão do cliente é nulo.');

      const itens = buildAnswerItems(perguntas);

      if (itens.length) await submitAnswers({ restauranteId, clienteId, itens });

      const feedbackPayload = {
        restauranteId,
        clienteId,
        nota: notaFinal,
        comentario: comentarioFinal,
        cliente_nome: clienteNome.trim(),
        cliente_telefone: clienteTelefone.trim(),
      };

      await saveFeedback(feedbackPayload);
      await finishSession(restauranteId, clienteId);
      setFinalizado(true);
    } catch (e) {
      console.error('Erro ao finalizar:', e);
      setFormError(getApiErrorMessage(e, 'Falha ao enviar suas respostas. Tente novamente.'));
    } finally {
      setEnviando(false);
    }
  }

  // estilos
  const baseBtn = `
    px-4 py-2 rounded border
    bg-white text-neutral-800 border-neutral-300
    hover:bg-neutral-100 active:bg-neutral-300
    focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2
    transition
  `;
  const selectedBtn = `
    !bg-emerald-50 !text-emerald-700 !border-emerald-600
    !font-semibold
    hover:!bg-emerald-100 active:!bg-emerald-200
  `;
  const primaryBtn = `
    px-6 py-3 rounded-lg
    bg-emerald-600 text-white
    hover:bg-emerald-700 active:bg-emerald-800
    disabled:bg-emerald-400 disabled:text-white/80 disabled:cursor-not-allowed
    focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2
    transition
  `;
  const iconBtn = `
    inline-flex items-center justify-center gap-2
    p-2 rounded-full border
    bg-white text-neutral-700 border-neutral-300
    hover:bg-neutral-100 active:bg-neutral-200
    focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2
    transition
  `;

  // render de inputs (com rádio/checkbox verticais)
  function renderInputPergunta(p) {
    const pid = getPerguntaId(p);
    const r = respostas[pid] || {};
    const errorVisible = showErrors && p?.obrigatoria && !isAnswered(p);

    switch (p.tipo) {
      case 'nps': {
        const selected = r.respostaNumero;
        return (
          <>
            <div className="flex flex-wrap gap-2 justify-center">
              {Array.from({ length: 11 }).map((_, n) => (
                <button
                  key={n}
                  type="button"
                  className={`${baseBtn} ${selected === n ? selectedBtn : ''}`}
                  onClick={() => setRespostaAtual(p, n)}
                >
                  {n}
                </button>
              ))}
            </div>
            {errorVisible && (
              <p className="mt-2 text-sm text-red-600 text-center">Esta pergunta é obrigatória.</p>
            )}
          </>
        );
      }
      case 'escala': {
        const selected = r.respostaNumero;
        return (
          <>
            <div className="flex gap-2 justify-center">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`${baseBtn} ${selected === n ? selectedBtn : ''}`}
                  onClick={() => setRespostaAtual(p, n)}
                >
                  {n}
                </button>
              ))}
            </div>
            {errorVisible && (
              <p className="mt-2 text-sm text-red-600 text-center">Esta pergunta é obrigatória.</p>
            )}
          </>
        );
      }
      case 'multipla_escolha': {
        const selected = r.respostaTexto || '';
        const optionsRaw = Array.isArray(p.opcoes) ? p.opcoes : [];
        const options = optionsRaw.map((o) =>
          typeof o === 'string' ? o : String(o?.label ?? o)
        );
        return (
          <>
            <div className="max-w-md mx-auto">
              <ul className="space-y-3">
                {options.map((opt) => (
                  <li key={opt} className="flex items-center gap-3">
                    <input
                      type="radio"
                      id={`${pid}-${opt}`}
                      name={`radio-${pid}`}
                      className="h-4 w-4 accent-emerald-600"
                      checked={selected === opt}
                      onChange={() => setRespostaAtual(p, opt)}
                    />
                    <label htmlFor={`${pid}-${opt}`} className="cursor-pointer select-none">
                      {opt}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
            {errorVisible && (
              <p className="mt-2 text-sm text-red-600 text-center">Esta pergunta é obrigatória.</p>
            )}
          </>
        );
      }
      case 'multipla_escolha_multi': {
        const selected = r.respostaLista || [];
        const optionsRaw = Array.isArray(p.opcoes) ? p.opcoes : [];
        const options = optionsRaw.map((o) =>
          typeof o === 'string' ? o : String(o?.label ?? o)
        );
        return (
          <>
            <div className="max-w-md mx-auto">
              <ul className="space-y-3">
                {options.map((opt) => {
                  const isOn = selected.includes(opt);
                  return (
                    <li key={opt} className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id={`${pid}-chk-${opt}`}
                        className="h-4 w-4 accent-emerald-600"
                        checked={isOn}
                        onChange={() => toggleMultiOption(p, opt)}
                      />
                      <label htmlFor={`${pid}-chk-${opt}`} className="cursor-pointer select-none">
                        {opt}
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
            {errorVisible && (
              <p className="mt-2 text-sm text-red-600 text-center">Esta pergunta é obrigatória.</p>
            )}
          </>
        );
      }
      case 'sim_nao': {
        const selected = r.respostaTexto || '';
        const opts = ['Sim', 'Não'];
        return (
          <>
            <div className="max-w-md mx-auto">
              <ul className="space-y-3">
                {opts.map((opt) => (
                  <li key={opt} className="flex items-center gap-3">
                    <input
                      type="radio"
                      id={`${pid}-${opt}`}
                      name={`radio-${pid}`}
                      className="h-4 w-4 accent-emerald-600"
                      checked={selected === opt}
                      onChange={() => setRespostaAtual(p, opt)}
                    />
                    <label htmlFor={`${pid}-${opt}`} className="cursor-pointer select-none">
                      {opt}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
            {errorVisible && (
              <p className="mt-2 text-sm text-red-600 text-center">Esta pergunta é obrigatória.</p>
            )}
          </>
        );
      }
      default:
        return (
          <>
            <textarea
              className="w-full max-w-2xl mx-auto border rounded p-3"
              rows={4}
              value={r.respostaTexto || ''}
              onChange={(e) => setRespostaAtual(p, e.target.value)}
              placeholder="Escreva sua resposta…"
            />
            {errorVisible && (
              <p className="mt-2 text-sm text-red-600 text-center">Esta pergunta é obrigatória.</p>
            )}
          </>
        );
    }
  }

  // telas base
  if (loading) return <div className="p-6 text-center">Carregando…</div>;
  if (erro) return <div className="p-6 text-center text-red-600">{erro}</div>;

  // final
  if (finalizado) {
    const isPromoter = Number(notaFinal) === 5;
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6">
        <div className="w-full max-w-lg bg-white rounded-2xl shadow p-8 text-center space-y-4">
          {isPromoter ? (
            <>
              <h1 className="text-3xl font-semibold">Muito obrigado! 🎉</h1>
              <p className="text-neutral-700">
                Ficamos felizes com a sua nota avaliação. Se puder, deixe também uma avaliação no Google,
                isso nos ajuda demais!
              </p>
              {googleLink ? (
                <a
                  href={googleLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-6 py-3 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 transition"
                >
                  Avaliar no Google ⭐
                </a>
              ) : (
                <p className="text-sm text-neutral-500">
                </p>
              )}
            </>
          ) : (
            <>
              <h1 className="text-3xl font-semibold">Obrigado!</h1>
              <p className="text-neutral-700">Seu feedback foi registrado com sucesso.</p>
            </>
          )}
        </div>
      </div>
    );
  }

  // step 0
  if (step === 0)
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow p-6 text-center">
          <h2 className="text-sm font-medium text-neutral-500 uppercase tracking-wider mb-2">
            Você está no
          </h2>
          <div className="mb-6">
            <h1
              className="
                mt-3
                text-3xl sm:text-4xl md:text-5xl
                font-extrabold tracking-tight leading-tight
                bg-gradient-to-r from-emerald-600 via-teal-600 to-sky-600
                bg-clip-text text-transparent
                drop-shadow
              "
            >
              {restaurante?.nome || `Restaurante #${restauranteSlug}`}
            </h1>
          </div>
          <button type="button" onClick={handleConfirmarEstabelecimento} className={primaryBtn}>
            Confirmar
          </button>
        </div>
      </div>
    );

  // step 1
  if (step === 1)
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <form onSubmit={handleIdentificarCliente} className="w-full max-w-md bg-white rounded-2xl shadow p-6">
          <h2 className="text-2xl font-bold mb-1 text-center">Antes de começar</h2>
          <p className="text-neutral-600 mb-6 text-center">
            Informe seu nome e telefone com DDD.
          </p>

          <label className="block text-sm font-medium mb-1">Nome</label>
          <input
            type="text"
            className="w-full border rounded px-3 py-2 mb-4"
            value={clienteNome}
            onChange={(e) => setClienteNome(e.target.value)}
            required
          />

          <label className="block text-sm font-medium mb-1">Telefone (com DDD)</label>
          <input
            type="tel"
            className="w-full border rounded px-3 py-2 mb-6"
            value={clienteTelefone}
            onChange={(e) => setClienteTelefone(e.target.value)}
            inputMode="numeric"
            pattern="\(?[1-9]{2}\)?\s?(?:9\d{4}|\d{4})-?\d{4}"
            placeholder="(11) 91234-5678"
            required
          />

          <button type="submit" className={primaryBtn}>
            Continuar
          </button>
        </form>
      </div>
    );

  // formulário: páginas + feedback
  return (
    <div className="min-h-screen bg-neutral-50 flex items-start justify-center p-4">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow overflow-hidden">
        <div className="h-2 bg-neutral-200">
          <div className="h-full bg-gradient-to-r from-emerald-500 to-sky-500 transition-all" style={{ width: `${progresso}%` }} />
        </div>

        <div className="p-6">
          <div className="mb-3">
            <button type="button" onClick={voltar} aria-label="Voltar" className={iconBtn} title="Voltar">
              <FaArrowLeft />
            </button>
          </div>

          {!showFeedback ? (
            <>
              <header className="mb-4 text-center">
                <p className="text-neutral-600">
                  Página {page + 1} de {totalPaginasDePerguntas}
                </p>
              </header>

              {/* Grupo de perguntas */}
              <div className="space-y-8">
                {grupoAtual.map((p) => (
                  <div key={getPerguntaId(p)} className="max-w-2xl mx-auto">
                    <h3 className="text-xl font-semibold mb-3 text-center">{p.texto}</h3>
                    {renderInputPergunta(p)}
                  </div>
                ))}
              </div>

              <div className="mt-8 flex items-center justify-center gap-3">
                <button type="button" onClick={voltar} className={baseBtn}>
                  Voltar
                </button>
                <button
                  type="button"
                  disabled={enviando || salvandoParcial || !grupoValido()}
                  onClick={avancar}
                  className={primaryBtn}
                >
                  {salvandoParcial
                    ? 'Salvando...'
                    : page < totalPaginasDePerguntas - 1
                      ? 'Próximo'
                      : 'Continuar'}
                </button>
              </div>

              {formError && (
                <p className="mt-3 text-sm text-red-600 text-center">
                  {formError}
                </p>
              )}

              {showErrors && !grupoValido() && (
                <p className="mt-3 text-sm text-red-600 text-center">
                  Responda todas as obrigatórias para continuar.
                </p>
              )}
            </>
          ) : (
            // feedback final
            <>
              <h3 className="text-xl font-semibold text-center mb-4">Deixe sua avaliação</h3>

              <div className="flex justify-center mb-6">
                {[1, 2, 3, 4, 5].map((n) => (
                  <FaStar
                    key={n}
                    size={32}
                    className={`mx-1 cursor-pointer ${n <= notaFinal ? 'text-yellow-400' : 'text-neutral-300'}`}
                    onClick={() => setNotaFinal(n)}
                  />
                ))}
              </div>

              <textarea
                className="w-full max-w-2xl mx-auto border rounded p-3 mb-6"
                rows={4}
                value={comentarioFinal}
                onChange={(e) => setComentarioFinal(e.target.value)}
                placeholder="Escreva seu comentário…"
              />

              <div className="flex items-center justify-center gap-3">
                <button type="button" onClick={voltar} className={baseBtn}>
                  Voltar
                </button>
                <button type="button" disabled={enviando || notaFinal === 0} onClick={avancar} className={primaryBtn}>
                  {enviando ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
              {formError && (
                <p className="mt-3 text-sm text-red-600 text-center">
                  {formError}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
